import { skipToken } from '@apollo/client/react'
import { Badge, Button } from '@mui/material'
import Linkify from 'linkify-react'
import { useState } from 'react'
import { Button as AriaButton } from 'react-aria-components'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router'
import { JsonLd } from 'react-schemaorg'
import type { ProfilePage } from 'schema-dts'
import IcBaselineBlock from '~icons/ic/baseline-block'
import IcBaselineExpandLess from '~icons/ic/baseline-expand-less'
import IcBaselineExpandMore from '~icons/ic/baseline-expand-more'
import IcBaselineFlag from '~icons/ic/baseline-flag'
import IcBaselineLanguage from '~icons/ic/baseline-language'
import IcBaselineMoreVert from '~icons/ic/baseline-more-vert'
import IcBaselinePlace from '~icons/ic/baseline-place'
import LogosFacebook from '~icons/logos/facebook'
import LogosRedditIcon from '~icons/logos/reddit-icon'
import RiTwitterXFill from '~icons/ri/twitter-x-fill'
import SimpleIconsPixiv from '~icons/simple-icons/pixiv'
import SkillIconsInstagram from '~icons/skill-icons/instagram'
import { MembershipIconCircled } from 'src/components/Membership/MembershipIcon'
import UserOpActions from 'src/components/OpActions/UserOpActions'
import { OutboundLink } from 'src/components/OutboundLink'
import QuotaBalance from 'src/components/Quota/QuotaBalance'
import { QuotaHelpIcon } from 'src/components/Quota/QuotaHelpIcon'
import UserAvatar from 'src/components/UserAvatar'
import UserFollowButton from 'src/components/UserFollowButton'
import { GrowthSlot, GrowthSlotName } from 'src/features/Growth/growth-slot'
import { EmailVerifyBanner } from 'src/features/User/ForceVerifyEmail/EmailVerifyBanner'
import { useMyInfo } from 'src/services/apollo/auth'
import type { User } from 'src/services/apollo/generated/graphql'
import { RefType } from 'src/services/apollo/generated/graphql'
import { useGetUserArtworkLikedCount } from 'src/services/apollo/user'
import { useCollections } from 'src/services/helpers/bookmarkCollection'
import {
  useUserFollowerCount,
  useUserFollowingMe,
} from 'src/stores/user-follow'
import { getAppEnv } from 'src/utils/config'
import { cx } from 'src/utils/cx'
import { formatNum } from 'src/utils/formatNum'
import { useIsMobile } from 'src/utils/hooks/useBreakpoint'
import { getImageThumbnailUrl } from 'src/utils/media'
import type { SocialNetworkKey } from 'src/utils/socialNetwork'
import { SOCIAL_NETWORKS, resolveSocialUrl } from 'src/utils/socialNetwork'
import { startEventGroup, track } from 'src/utils/tracker'
import { getSafeLinkUrl } from 'src/utils/urlCheck'
import {
  userHasMembership,
  userHasNotVerifiedEmail,
  useShowEmailVerificationDot,
} from 'src/utils/user'
import { dialogBlockConfirm } from '../BlockList/BlockConfirmDialog'
import { UITooltipResponsive } from '../HelpIcon'
import { openMembershipMoreCreditsModal } from '../Membership/MembershipMoreCreditsModal'
import { QuestInProfileV2 } from '../Quest/v2/QuestInProfileV2'
import { dialogReport } from '../Report/ReportDialog'
import { UIButton } from '../ui/Button'
import { UIMenu, UIMenuItem, UIMenuTrigger } from '../ui/Menu'
import Text from '../ui/Text'
import { FollowType } from './constants'
import EditAvatar from './EditAvatar'
import { ProfileCover } from './ProfileCoverEdit'
import { useUserProfileContext } from './UserProfile.context'
import { UserVerificationInfo } from './UserVerificationInfo'

export default function UserProfileInfo() {
  const { isMyProfile } = useUserProfileContext()

  return (
    <div className="flex flex-col gap-8">
      <UserIntroSection />
      {isMyProfile && <CreditInfoSection />}
    </div>
  )
}

/**
 * ==== User Intro Section =====================================================
 */

function UserIntroSection() {
  const {
    user,
    profile: {
      bio,
      location,
      website,
      twitterId,
      instagramId,
      facebookId,
      redditId,
      pixivId,
    },
    isMyProfile,
  } = useUserProfileContext()
  const SOCIAL_ICONS: Record<SocialNetworkKey, React.ReactNode> = {
    twitterId: (
      <RiTwitterXFill className="size-full text-black dark:text-white" />
    ),
    instagramId: <SkillIconsInstagram className="size-full" />,
    facebookId: <LogosFacebook className="size-full" />,
    redditId: <LogosRedditIcon className="size-full" />,
    pixivId: <SimpleIconsPixiv className="size-full text-[#0099FA]" />,
  }

  const socialValues: Record<SocialNetworkKey, string | null | undefined> = {
    twitterId,
    instagramId,
    facebookId,
    redditId,
    pixivId,
  }

  const socialLinks = SOCIAL_NETWORKS.map(meta => {
    const resolved = resolveSocialUrl(meta, socialValues[meta.key])
    if (!resolved) return null
    return {
      key: meta.key,
      icon: SOCIAL_ICONS[meta.key],
      ...resolved,
    }
  }).filter(Boolean)

  const { data: me } = useMyInfo()
  const { t } = useTranslation(['user', 'membership', 'common'])

  const [followerCount] = useUserFollowerCount(user.id, user.followerCount || 0)
  const [followingMe] = useUserFollowingMe(user.id, user.followingMe ?? false)

  // Reduce artwork liked count's refetch frequency
  const { data: artworkLikedCount } = useGetUserArtworkLikedCount(
    user.id ?? skipToken,
    {
      context: {
        refetchOnFocus: false,
        refetchOnReconnect: false,
      },
    },
  )
  const isMember = userHasMembership(user)

  const [showDot, updateDotState] = useShowEmailVerificationDot(
    'user-profile-btn',
    me,
  )

  const appEnv = getAppEnv()

  return (
    <>
      <ProfileCover
        user={user}
        isMe={isMyProfile}
        allowEdit
        hoverToEdit={Boolean(user?.coverMedia)}
        className="-mx-4 msm:-mx-6 mmd:-mx-12 mmd:-mt-12 mmd:rounded-t-lg"
      />

      <div className="-mt-8 first:mt-0 pt-4 msm:pt-6 mmd:pt-12 mmd:first:pt-0 grid grid-cols-[auto_1fr] grid-rows-[auto_auto] gap-x-3 msm:gap-x-4 gap-y-2 msm:gap-y-3">
        {/* Avatar */}
        <div className="row-span-full col-start-1 self-center">
          {isMyProfile && isMember ? (
            <EditAvatar
              avatarClassName="mmd:size-[120px] size-20"
              displayBadgeRemark
            />
          ) : (
            <UserAvatar
              avatarImageQuality="thumbnail"
              user={user}
              className="mmd:size-[120px] size-20"
              displayBadgeRemark
            />
          )}
        </div>
        <ProfileJsonLD user={user} />
        {/* User name */}
        <h1 className="row-start-1 col-start-2 self-end flex gap-3 items-center">
          <span className="text-xl msm:text-2xl font-semibold">
            {user.displayName || user.username}
          </span>

          <UserVerificationInfo accountType={user.accountType} />
          {isMember && <MembershipBadge />}
          {!isMyProfile && followingMe && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-800 text-primary-light">
              {t('user:follows-you')}
            </span>
          )}
        </h1>
        {/* Buttons */}
        <div className="row-start-2 col-start-2 self-start flex flex-wrap gap-3 items-center">
          {isMyProfile ? (
            <>
              <Button
                component={Link}
                to="/profile/edit"
                variant="outlined"
                onClick={() => {
                  track('edit_profile_click')
                }}
                className="px-8 rounded-md shadow-none"
              >
                <span className="capitalize">{t('user:edit-profile')}</span>
              </Button>
              {!isMember && (
                <Button
                  component={Link}
                  to="/profile/edit"
                  variant="outlined"
                  onClick={e => {
                    track('banner_change_click')
                    if (!isMember) {
                      e.preventDefault()
                      openMembershipMoreCreditsModal({
                        trackingLocation: 'banner_change',
                      })
                    }
                  }}
                  className="px-8 rounded-md"
                >
                  <span className="normal-case">{t('user:edit-banner')}</span>
                </Button>
              )}
              {userHasNotVerifiedEmail(me) && (
                <Badge
                  classes={{ badge: 'top-0.5 right-0.5' }}
                  variant="dot"
                  color="error"
                  invisible={!showDot}
                >
                  <Button
                    variant="outlined"
                    component={Link}
                    to="/profile/edit/accounts"
                    onClick={() => updateDotState()}
                    className="rounded-md"
                  >
                    {t('user:email.verify-for-credit-discount')}
                  </Button>
                </Badge>
              )}
            </>
          ) : (
            <UserFollowButton
              user={user}
              userId={user.id}
              initialValue={user.followedByMe}
              initialFollowingMe={user.followingMe}
              initialCount={user.followerCount}
              scene="profile"
            />
          )}
          {(!appEnv.isProd || me?.isAdmin) && user && (
            <UserOpActions user={user} />
          )}
          {!isMyProfile && me && (
            <UserExtraActions
              userId={user.id}
              username={user.username}
              blockStatus={user?.blockStatus}
            />
          )}
        </div>
      </div>

      {bio && <UserBio bio={bio} />}

      {(location || website || socialLinks.length > 0) && (
        <div className="flex flex-wrap gap-x-4 gap-y-1 items-center -my-4 text-sm text-primary-light">
          {website && (
            <div className="flex gap-1 items-center">
              <span className="text-primary-light">
                <IcBaselineLanguage className="size-5" />
              </span>
              <OutboundLink
                href={website}
                className="text-primary-light hover:underline"
              >
                {website}
              </OutboundLink>
            </div>
          )}
          {socialLinks.map(link =>
            link ? (
              <OutboundLink
                key={link.key}
                href={
                  link.isSafe
                    ? link.url
                    : getSafeLinkUrl(link.url, 'social-profile')
                }
                className="flex-none size-5"
                onClick={() => {
                  track('contact_click')
                }}
              >
                {link.icon}
              </OutboundLink>
            ) : null,
          )}
          {location && (
            <div className="flex gap-1 items-center">
              <span className="text-primary-light">
                <IcBaselinePlace className="size-5" />
              </span>
              {location}
            </div>
          )}
        </div>
      )}

      <div className="flex flex-wrap gap-x-4 gap-y-2 items-center">
        <Link
          to={`/@${user.username}/${FollowType.following}`}
          className="flex gap-1 items-baseline hover:underline"
          onClick={() => {
            track('following_count_click')
          }}
        >
          <span className="font-bold">
            {(user.followingCount || 0).toLocaleString()}
          </span>
          <span className="text-sm text-primary-light">
            {t('user:following-count-label')}
          </span>
        </Link>

        <Link
          to={`/@${user.username}/${FollowType.follower}`}
          className="flex gap-1 items-baseline hover:underline"
          onClick={() => {
            track('follower_count_click')
          }}
        >
          <span className="font-bold">{followerCount.toLocaleString()}</span>
          <span className="text-sm text-primary-light">
            {t('user:follower-count-label')}
          </span>
        </Link>

        {artworkLikedCount != null && (
          <span className="flex gap-1 items-baseline">
            <span className="font-bold text-theme-primary">
              {formatNum(artworkLikedCount)}
            </span>
            <span className="text-sm text-primary-light">
              {t('user:artwork-liked-count-label')}
            </span>
          </span>
        )}
      </div>
    </>
  )
}

function UserBio(props: { bio: string }) {
  const { bio } = props
  const isMobile = useIsMobile()
  const [expanded, setExpanded] = useState(false)
  const { t } = useTranslation(['user'])

  const clamped = !expanded
  const threshold = (bio?.trim().length ?? 0) > (isMobile ? 160 : 320)
  const showToggle = threshold

  return (
    <div>
      <h2
        className={cx(
          'whitespace-pre-wrap',
          clamped &&
            'break-words text-primary-light line-clamp-6 md:line-clamp-12',
        )}
      >
        <Linkify
          options={{
            attributes: {
              rel: 'nofollow',
            },
          }}
        >
          {bio}
        </Linkify>
      </h2>
      {showToggle && (
        <AriaButton
          onPress={() => setExpanded(v => !v)}
          className="mt-2 text-sm font-medium text-primary hover:underline"
        >
          {expanded ? (
            <span className="inline-flex items-center gap-1">
              <IcBaselineExpandLess className="size-4" />
              {t('user:show-less-bio')}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1">
              <IcBaselineExpandMore className="size-4" />
              {t('user:show-more-bio')}
            </span>
          )}
        </AriaButton>
      )}
    </div>
  )
}

/**
 * ==== Credit & Quest Section ==========================================================
 */
function CreditInfoSection() {
  const { t } = useTranslation(['membership', 'user'])

  const handleMembershipClick = () => {
    const trackingLocation = 'edit-membership'
    track('enter_member_click', { location: trackingLocation })
    startEventGroup('member_source', trackingLocation, {
      persistent: true,
    })
  }

  return (
    <section>
      <div className="flex flex-wrap items-center mb-3 gap-4">
        <span className="flex gap-2 items-center">
          <QuotaBalance trackingLocation="profile-plan" />
          <QuotaHelpIcon />
        </span>
        <GrowthSlot
          name={GrowthSlotName.UserProfileMembershipButton}
          className="contents"
        >
          <Button
            component={Link}
            to="/membership/plans"
            variant="contained"
            className={cx(
              'w-[155px] px-4 text-white rounded-md',
              'bg-purple-600',
            )}
            onClick={handleMembershipClick}
          >
            {t('membership:membership.label', { context: 'long' })}
          </Button>
        </GrowthSlot>
      </div>
      <EmailVerifyBanner className="mb-3 max-w-[500px]" />
      <div className="grid gap-3 grid-cols-1 mmd:grid-cols-2 mlg:grid-cols-3">
        <QuestInProfileV2 />
      </div>
    </section>
  )
}

function UserExtraActions(
  props: {
    userId: string
    username: string
    blockStatus?: string
  } & StyleProps,
) {
  const { userId, className, blockStatus, username } = props

  const action = (() => {
    if (
      blockStatus === 'blocked_by_other' ||
      blockStatus === 'blocked_each_other'
    ) {
      return undefined
    }

    const action = blockStatus === 'none' ? 'block' : 'unblock'

    return action
  })()

  const onReport = async () => {
    dialogReport(RefType.User, userId)
  }

  const onBlock = async () => {
    if (!action) return
    dialogBlockConfirm(action, userId, username, choice => {
      if (choice === 'confirm') {
        window.location.reload()
      }
    })
  }

  const menuItems = [
    {
      id: 'report',
      icon: <IcBaselineFlag className="size-5" />,
      children: <Text i18nKey="user:profile.action.report" />,
      onAction: onReport,
    },
  ]

  if (action) {
    menuItems.push({
      id: 'block',
      icon: <IcBaselineBlock className="size-5" />,
      children: <Text i18nKey={`user:profile.action.${action}`} />,
      onAction: onBlock,
    })
  }

  return (
    <>
      <UIMenuTrigger>
        <UIButton variant="outlined" className={cx(className, 'h-[36.5px]')}>
          <IcBaselineMoreVert className="size-5" />
        </UIButton>
        <UIMenu items={menuItems} placement="bottom">
          {props => <UIMenuItem {...props} />}
        </UIMenu>
      </UIMenuTrigger>
    </>
  )
}

function ProfileJsonLD(props: { user: User }) {
  const { profile } = useUserProfileContext()
  const { collections } = useCollections({
    userId: props.user.id,
  })

  const name = props.user.displayName || props.user.username
  // `window.location.origin` is unavailable during SSR. Use the
  // canonical pixai.art origin as the fallback so JSON-LD can be
  // rendered server-side; once the bundle hydrates the value will
  // reconcile against the live `window.location.origin` (always
  // pixai.art on the deployed site).
  const origin =
    typeof window !== 'undefined' ? window.location.origin : 'https://pixai.art'
  return (
    <JsonLd<ProfilePage>
      item={{
        '@context': 'https://schema.org',
        '@type': 'ProfilePage',
        mainEntity: {
          '@type': 'Person',
          name: name,
          url: `${origin}/@${props.user.username}/artworks`,
          image: getImageThumbnailUrl(props.user.avatarMediaId),
          description: profile?.bio,
          subjectOf: {
            '@type': 'CreativeWork',
            name: 'Collections',
            hasPart: collections?.map(bookmark => ({
              '@type': 'Collection',
              name: bookmark.name,
              thumbnailUrl: getImageThumbnailUrl(bookmark.coverMediaId),
              url: `${origin}/@${props.user.username}/collection/${bookmark.id}`,
              creator: {
                '@type': 'Person',
                name: name,
              },
              datePublished: bookmark.createdAt,
            })),
          },
        },
      }}
    />
  )
}

const MembershipBadge = () => {
  const { t } = useTranslation('membership')
  const navigate = useNavigate()
  const onClick = () => {
    const trackingLocation = 'subscription-user-profile'
    track('enter_member_click', { location: trackingLocation })
    startEventGroup('member_source', trackingLocation, {
      persistent: true,
    })

    navigate('/membership/plans')
  }

  return (
    <UITooltipResponsive
      trigger={() => {
        return (
          <AriaButton className="w-5 h-5" onClick={onClick}>
            <MembershipIconCircled className="w-full h-full" />
          </AriaButton>
        )
      }}
    >
      <div className="px-3 py-1.5 text-sm font-medium text-gray-900 bg-white rounded-lg">
        {t('membership:membership.badge.label')}
      </div>
    </UITooltipResponsive>
  )
}
